// import './App.css';
import React, { useEffect, useRef, useState } from "react";

function MapComponent() {
    // Holds a direct reference to the EMC web component instance.
    // We use this ref to dispatch custom DOM events that invoke EMC methods.

    const emcRef = useRef(null);

    // Initialization settings passed to EMC when the map is first configured.
    // These values define the base map source plus the backend endpoints
    // required for secured map and lookup operations.
    const [backgroundServiceURL, setBackgroundServiceURL] = useState(
        "http://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}",
    );
    const [backgroundServiceType, setBackgroundServiceType] =
        useState("google_sat");
    const [apiBackendURL, setApiBackendURL] = useState(
        "",
    );
    const [tokenApiBackendURL, setTokenApiBackendURL] = useState(
        "",
    );
    const [clientId, setClientId] = useState(
        "",
    );
    const [clientSecret, setClientSecret] = useState(
        "",
    );
    const [grantType, setGrantType] = useState("client_credentials");

    // Stores response payloads returned by EMC result events so they can be
    // rendered in the demo UI for inspection and testing.
    const [featureData, setFeatureData] = useState(null);
    const [lookupData, setLookupData] = useState(null);

    useEffect(() => {});

    // Initializes EMC with the selected map and backend configuration.
    // The React app does not call EMC methods directly; instead, it sends
    // a custom "initializeMap" event whose detail object acts as the method input.
    const setURLS = () => {
        const customInitializeMapEvent = new CustomEvent("initializeMap", {
            detail: {
                BackgroundServiceURL: backgroundServiceURL,
                BackgroundServiceType: backgroundServiceType,
                APIBackendURL: apiBackendURL,
                TokenApiBackendURL: tokenApiBackendURL,
                ClientId: clientId,
                ClientSecret: clientSecret,
                GrantType: grantType,
            },
        });

        emcRef.current.dispatchEvent(customInitializeMapEvent);
    };

    // Demonstrates the EMC zoomToLayer(MapLayerCode) method.
    // This focuses the map on the full extent of the requested layer.
    const zoomToLayerClick = () => {
        const customZoomToLayerEvent = new CustomEvent("zoomToLayer", {
            detail: { MapLayerCode: 1 },
        });

        emcRef.current.dispatchEvent(customZoomToLayerEvent);
    };

    // Demonstrates zooming to a specific feature in the Governorate layer.
    // The EMC identifies the feature by the pair: layer code + feature id.
    const handleZoomToGovFeature = () => {
        const event = new CustomEvent("zoomToFeature", {
            detail: { MapLayerCode: 1, FeatureID: 19 },
        });

        emcRef.current?.dispatchEvent(event);
    };

    // Demonstrates zooming to a Police Station feature.
    const handleZoomToPoliceStationFeature = () => {
        const event = new CustomEvent("zoomToFeature", {
            detail: { MapLayerCode: 2, FeatureID: 272 },
        });

        emcRef.current?.dispatchEvent(event);
    };

    // Demonstrates zooming to a Neighborhood feature.
    const handleZoomToNeighborhoodFeature = () => {
        const event = new CustomEvent("zoomToFeature", {
            detail: { MapLayerCode: 3, FeatureID: 5544 },
        });

        emcRef.current?.dispatchEvent(event);
    };

    // Demonstrates zooming to a Street feature.
    const handleZoomToStreetFeature = () => {
        const event = new CustomEvent("zoomToFeature", {
            detail: { MapLayerCode: 4, FeatureID: 2 },
        });

        emcRef.current?.dispatchEvent(event);
    };

    // Demonstrates the identifyFeature(MapLayerCode, FeatureID) workflow.
    // EMC performs the lookup and emits an "identifyFeatureResult" event
    // containing the feature attributes, which are then stored in React state.
    const handleIdentifyFeature = () => {
        const event = new CustomEvent("identifyFeature", {
            detail: { MapLayerCode: 9, FeatureID: 123456789012345 },
        });

        emcRef.current?.dispatchEvent(event);

        // Listen once for the EMC callback event to avoid duplicate subscriptions
        // when the demo action is executed multiple times.
        const listener = (e) => {
            setFeatureData(e.detail);
        };

        emcRef.current?.addEventListener("identifyFeatureResult", listener, {
            once: true,
        });
    };

    // Demonstrates the EMC zoomToLatLong(lat, long, zoomLevel) method.
    // This is useful for centering the map on arbitrary coordinates rather
    // than on a predefined layer or feature.
    const zoomToLatLongClick = () => {
        const customZoomToLatLongEvent = new CustomEvent("zoomToLatLong", {
            detail: {
                Lat: 30.016977869507677,
                Long: 31.422252710211197,
                ZoomLevel: 18,
            },
        });

        emcRef.current.dispatchEvent(customZoomToLatLongEvent);
    };

    // Requests the full governorates lookup list from EMC.
    // The control answers asynchronously through "getGovernorates Result".
    const GetGovernorates = () => {
        const event = new CustomEvent("getGovernorates");

        emcRef.current?.dispatchEvent(event);

        const listener = (e) => {
            setLookupData(e.detail);
        };

        emcRef.current?.addEventListener(`getGovernorates Result`, listener, {
            once: true,
        });
    };

    // Requests police stations filtered by governorate id.
    // This illustrates how EMC supports cascading administrative lookups.
    const GetPoliceStations = () => {
        const event = new CustomEvent("getPoliceStations", {
            detail: { gov_id: 19 },
        });

        emcRef.current?.dispatchEvent(event);

        const listener = (e) => {
            setLookupData(e.detail);
        };

        emcRef.current?.addEventListener(`getPoliceStations Result`, listener, {
            once: true,
        });
    };

    // Requests neighborhoods filtered by police station id.
    const GetNeighborhoods = () => {
        const event = new CustomEvent("getNeighborhoods", {
            detail: { policeStation_id: 272 },
        });

        emcRef.current?.dispatchEvent(event);

        const listener = (e) => {
            setLookupData(e.detail);
        };

        emcRef.current?.addEventListener(`getNeighborhoods Result`, listener, {
            once: true,
        });
    };

    // Requests streets filtered by police station id.
    const GetStreets = () => {
        const event = new CustomEvent("getStreets", {
            detail: { policeStation_id: 272 },
        });

        emcRef.current?.dispatchEvent(event);

        const listener = (e) => {
            setLookupData(e.detail);
        };

        emcRef.current?.addEventListener(`getStreets Result`, listener, {
            once: true,
        });
    };

    return (
        <div>
            <h1>React Testing App - Enterprise Map Control</h1>

            {/*
        EMC is consumed here as a custom web component.
        React manages the surrounding form state and user interactions,
        while the map behavior itself is triggered through custom events.
      */}
            <enterprise-map-control ref={emcRef}></enterprise-map-control>

            {/* set URLS */}
            <div className="card mx-5 mb-2">
                <div
                    className="card-header bg-primary text-white fw-bold"
                    style={{ direction: "rtl" }}
                >
                    <span>الوظيفة: </span>
                    إضافة URLs
                </div>

                <div className="card-body bg-light">
                    <div className="d-flex align-items-end flex-wrap">
                        <div className="d-flex flex-column mx-2">
                            <label className="form-label">BackgroundServiceURL:</label>
                            <input
                                type="text"
                                className="form-control"
                                value={backgroundServiceURL}
                                onChange={(e) => setBackgroundServiceURL(e.target.value)}
                            />
                        </div>

                        <div className="d-flex flex-column mx-2">
                            <label className="form-label">BackgroundServiceType:</label>
                            <select
                                className="form-select"
                                value={backgroundServiceType}
                                onChange={(e) => setBackgroundServiceType(e.target.value)}
                            >
                                <option value="osm">osm</option>
                                <option value="google_maps">google_maps</option>
                                <option value="google_sat">google_sat</option>
                            </select>
                        </div>

                        <div className="d-flex flex-column mx-2">
                            <label className="form-label">APIBackendURL:</label>
                            <input
                                type="text"
                                className="form-control"
                                value={apiBackendURL}
                                onChange={(e) => setApiBackendURL(e.target.value)}
                            />
                        </div>

                        <div className="d-flex flex-column mx-2">
                            <label className="form-label">TokenApiBackendURL:</label>
                            <input
                                type="text"
                                className="form-control"
                                value={tokenApiBackendURL}
                                onChange={(e) => setTokenApiBackendURL(e.target.value)}
                            />
                        </div>

                        <div className="d-flex flex-column mx-2">
                            <label className="form-label">clientId:</label>
                            <input
                                type="text"
                                className="form-control"
                                value={clientId}
                                onChange={(e) => setClientId(e.target.value)}
                            />
                        </div>

                        <div className="d-flex flex-column mx-2">
                            <label className="form-label">clientSecret:</label>
                            <input
                                type="text"
                                className="form-control"
                                value={clientSecret}
                                onChange={(e) => setClientSecret(e.target.value)}
                            />
                        </div>

                        <div className="d-flex flex-column mx-2">
                            <label className="form-label">grantType:</label>
                            <input
                                type="text"
                                className="form-control"
                                value={grantType}
                                onChange={(e) => setGrantType(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="card-footer bg-light" style={{ direction: "rtl" }}>
                    <div className="mx-2 mb-1">
                        <button onClick={setURLS} className="btn btn-primary">
                            Set
                        </button>
                    </div>
                </div>
            </div>

            {/* Zoom to Layer */}
            <div className="card mx-5 mb-2" style={{ direction: "rtl" }}>
                <div className="card-header bg-primary text-white fw-bold">
                    <span>الوظيفة: </span>
                    تكبير طبقة معينة على الخريطة
                </div>

                <div className="card-body bg-light">
                    <p>
                        الوصف: تكبير وعرض الطبقة المحددة على الخريطة بشكل كامل باستخدام معرف
                        الطبقة.
                    </p>

                    <p className="text-primary fw-bold">
                        MapComponentMethod: zoomToLayer(MapLayerCode)
                    </p>

                    <code>example =&gt; zoomToLayer(1)</code>
                </div>

                <div className="card-footer bg-light">
                    <button onClick={zoomToLayerClick} className="btn btn-primary mx-2">
                        Zoom to Admin Layer
                    </button>
                </div>
            </div>

            {/* Zoom To Lat Long */}
            <div className="card mx-5 mb-2" style={{ direction: "rtl" }}>
                <div className="card-header bg-primary text-white fw-bold">
                    <span>الوظيفة: </span>
                    الذهاب الي نقطة جغرافية علي الخريطة
                </div>

                <div className="card-body bg-light">
                    <p>الوصف: الذهاب الي نقطة جغرافية باستخدام (Lat , Long, zoomlevel)</p>

                    <p className="text-primary fw-bold">
                        MapComponentMethod: zoomToLatLong(lat, long, zoomLevel)
                    </p>

                    <code>
                        example =&gt; zoomToLatLong(30.016977869507677, 31.422252710211197,
                        18)
                    </code>
                </div>

                <div className="card-footer bg-light">
                    <button onClick={zoomToLatLongClick} className="btn btn-primary mx-2">
                        Zoom To Lat Long
                    </button>
                </div>
            </div>

            {/* Zoom To Feature */}
            <div className="card mx-5 mb-2" style={{ direction: "rtl" }}>
                <div className="card-header bg-primary text-white fw-bold">
                    <span>الوظيفة: </span>
                    تكبير معلم محدد داخل طبقة على الخريطة
                </div>

                <div className="card-body bg-light">
                    <p>
                        الوصف: تكبير وعرض معلم (Feature) معين باستخدام معرف الطبقة ومعرف
                        المعلم.
                    </p>

                    <p className="text-primary fw-bold">
                        MapComponentMethod: zoomToFeature(MapLayerCode, FeatureID)
                    </p>

                    <p>
                        For Governorate Layer =&gt; MapLayerCode = 1 <br />
                        <code>example =&gt; zoomToFeature(1, 19)</code>
                    </p>

                    <p>
                        For Police Station Layer =&gt; MapLayerCode = 2 <br />
                        <code>example =&gt; zoomToFeature(2, 272)</code>
                    </p>

                    <p>
                        For Neighborhood Layer =&gt; MapLayerCode = 3 <br />
                        <code>example =&gt; zoomToFeature(3, 5544)</code>
                    </p>

                    <p>
                        For Street Layer =&gt; MapLayerCode = 4 <br />
                        <code>example =&gt; zoomToFeature(4, 2)</code>
                    </p>
                </div>

                <div className="card-footer bg-light">
                    <button
                        onClick={handleZoomToStreetFeature}
                        className="btn btn-primary mx-2"
                    >
                        Zoom To Street Feature
                    </button>

                    <button
                        onClick={handleZoomToNeighborhoodFeature}
                        className="btn btn-primary mx-2"
                    >
                        Zoom To Neighborhood Feature
                    </button>

                    <button
                        onClick={handleZoomToPoliceStationFeature}
                        className="btn btn-primary mx-2"
                    >
                        Zoom To Police Station Feature
                    </button>

                    <button
                        onClick={handleZoomToGovFeature}
                        className="btn btn-primary mx-2"
                    >
                        Zoom To Gov Feature
                    </button>
                </div>
            </div>

            {/* Identify Feature */}
            <div className="card mx-5 mb-2" style={{ direction: "rtl" }}>
                <div className="card-header bg-primary text-white fw-bold">
                    <span>الوظيفة: </span>
                    التعرف على معلم محدد في طبقة وإظهار تفاصيله
                </div>

                <div className="card-body bg-light">
                    <p>
                        الوصف: إظهار بيانات ومعلومات المعلم (Feature) عند تحديده باستخدام
                        معرف الطبقة ومعرف المعلم.
                    </p>

                    <p className="text-primary fw-bold">
                        MapComponentMethod: identifyFeature(MapLayerCode, FeatureID)
                    </p>

                    <p>
                        <code>example =&gt; identifyFeature(9, 123456789012345)</code>
                    </p>

                    {featureData && (
                        <p style={{ direction: "ltr" }}>
                            <span className="text-danger"> output =&gt; </span>
                            <pre style={{ margin: 0 }}>
                {JSON.stringify(featureData, null, 2)}
              </pre>
                        </p>
                    )}
                </div>

                <div className="card-footer bg-light">
                    <button
                        onClick={handleIdentifyFeature}
                        className="btn btn-primary mx-2"
                    >
                        Identify Feature
                    </button>
                </div>
            </div>

            {/* Lookup Section */}
            <div className="card mx-5 mb-2" style={{ direction: "rtl" }}>
                <div className="card-header bg-primary text-white fw-bold">
                    <span>الوظيفة: </span>
                    عرض البيانات المرتبطة بمعلم (Feature) معين
                </div>

                <div className="card-body bg-light">
                    <p>
                        الوصف: عرض البيانات المرتبطة بمعلم (Feature) معين بمعلومية معلم أخر
                    </p>

                    <p className="text-primary fw-bold">
                        MapComponentMethod: getGovernorates
                    </p>

                    <p>
                        Get Governorates List <br />
                        <code>()example =&gt; getGovernorates</code>
                    </p>

                    <p className="text-primary fw-bold">
                        MapComponentMethod: getPoliceStations(gov_id)
                    </p>

                    <p>
                        Get Police Stations List by governorate id <br />
                        <code>example =&gt; getPoliceStations(19)</code>
                    </p>

                    <p className="text-primary fw-bold">
                        MapComponentMethod: getNeighborhoods(policeStation_id)
                    </p>

                    <p>
                        Get Neighborhoods List by Police Station Id <br />
                        <code>example =&gt; getNeighborhoods(272)</code>
                    </p>

                    <p className="text-primary fw-bold">
                        MapComponentMethod: getStreets(policeStation_id)
                    </p>

                    <p>
                        Get Streets List by Police Station Id <br />
                        <code>example =&gt; getStreets(272)</code>
                    </p>

                    {lookupData && (
                        <div className="card-body bg-light">
                            <p>البيانات الحالية:</p>
                            <code>
                <pre style={{ margin: 0 }}>
                  {JSON.stringify(lookupData, null, 2)}
                </pre>
                            </code>
                        </div>
                    )}
                </div>

                <div className="card-footer bg-light">
                    <button onClick={GetStreets} className="btn btn-primary mx-2">
                        Get Streets List By PoliceStation Id
                    </button>

                    <button onClick={GetNeighborhoods} className="btn btn-primary mx-2">
                        Get Neighborhoods List By PoliceStation Id
                    </button>

                    <button onClick={GetPoliceStations} className="btn btn-primary mx-2">
                        Get PoliceStations List By Governorate Id
                    </button>

                    <button onClick={GetGovernorates} className="btn btn-primary mx-2">
                        Get Governorates List
                    </button>
                </div>
            </div>
        </div>
    );
}

export default MapComponent;
